import React from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_PRODUCTS } from "../../data/product";
import { generateSlug } from "../../utils/slugUtils";
import { useTypographyHelpers } from "../../contexts/DesignContext";
import { getProductKey } from "./detail/productUtils";

// Single Product Card Component
const ProductCard = ({
  image,
  title,
  abstract,
  imageAlt = "",
  ctaText = "Learn More",
  ctaUrl = "#",
  onClick = null,
  product = null,
  primaryFeatureTag = null,
}) => {
  const { t } = useTranslation("home");
  const { getUIStyles } = useTypographyHelpers();
  const uiStyles = getUIStyles();

  // Get interactive styles for hover effects
  const interactiveStyles = {
    hoverHighlight: "hover:text-violet-300 transition-colors duration-300",
  };
  const handleLearnMoreClick = (e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    if (product) {
      const slug = generateSlug(product.originalTitle || product.title);
      window.location.href = `/products/${slug}`;
    }
  };

  const handleCtaClick = (e) => {
    e.stopPropagation(); // Prevent card click when CTA is clicked
    window.open(ctaUrl, "_blank");
  };

  return (
    <div className="bg-gray-800/30 backdrop-blur border border-gray-700/50 rounded-3xl p-6 hover:bg-gray-800/40 hover:border-violet-400/50 transition-all duration-300 group">
      {/* Product Image */}
      <div
        className="w-full h-64 mb-6 rounded-2xl overflow-hidden bg-gray-700/30 cursor-pointer"
        onClick={handleLearnMoreClick}
      >
        <img
          src={image}
          alt={imageAlt || title}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            e.target.src =
              "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=500&h=400&fit=crop";
            e.target.style.opacity = "0.7";
          }}
        />
      </div>

      {/* Feature Tag */}
      {primaryFeatureTag && (
        <div className="mb-3">
          <span
            className={`inline-block px-3 py-1 bg-violet-600/20 text-violet-300 ${uiStyles.tag} rounded-full border border-violet-600/30`}
          >
            {primaryFeatureTag}
          </span>
        </div>
      )}

      {/* Product Title */}
      <h3
        className={`text-2xl font-semibold text-white mb-4 ${interactiveStyles.hoverHighlight}`}
      >
        {title}
      </h3>

      {/* Product Abstract */}
      <p className="text-base text-gray-300 leading-relaxed mb-6">{abstract}</p>

      {/* Buttons Container */}
      <div className="space-y-3">
        {/* Learn More Button - navigates to single product view */}
        <button
          onClick={handleLearnMoreClick}
          className="w-full bg-gray-700/50 hover:bg-gray-700/70 text-white px-6 py-3 rounded-2xl font-medium transition-all duration-300 border border-gray-600/50 hover:border-violet-400/50"
        >
          {t("productIntro.learnMore")}
        </button>

        {/* Call to Action Button - opens external product */}
        <button
          onClick={handleCtaClick}
          className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white px-6 py-3 rounded-2xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center gap-2"
        >
          <span>{ctaText}</span>
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Product Header Component
const ProductHeader = () => {
  const { t } = useTranslation("home");
  return (
    <div className="text-center mb-12">
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 hover:text-violet-300 transition-colors duration-300">
        {t("productIntro.heading")}
      </h2>
      <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
        {t("productIntro.subtitle")}
      </p>
    </div>
  );
};

// Product Grid Component
const ProductGrid = ({ products, onProductSelect }) => {
  const { t } = useTranslation("products");

  // Translate product data
  const translatedProducts = products.map((product) => {
    const productKey = getProductKey(product.title);
    const translatedProduct = t(productKey, {
      returnObjects: true,
      defaultValue: {},
    });

    return {
      ...product,
      originalTitle: product.title, // Preserve for slug generation
      title: translatedProduct.title || product.title,
      abstract: translatedProduct.abstract || product.abstract,
      primaryFeatureTag:
        translatedProduct.primaryFeatureTag || product.primaryFeatureTag,
      ctaText: translatedProduct.ctaText || product.ctaText,
      imageAlt: translatedProduct.imageAlt || product.imageAlt,
    };
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {translatedProducts.map((product) => (
        <ProductCard
          key={product.id}
          image={product.image}
          title={product.title}
          abstract={product.abstract}
          imageAlt={product.imageAlt}
          ctaText={product.ctaText}
          ctaUrl={product.ctaUrl}
          onClick={onProductSelect}
          product={product}
          primaryFeatureTag={product.primaryFeatureTag}
        />
      ))}
    </div>
  );
};

// Main ProductIntro Component
export default function ProductIntro({
  products = [],
  username = null,
  userImageUrl = null,
  logoUrl,
  onProductSelect = null,
  showHeader = true, // New prop to control header display
}) {
  const displayProducts = products.length > 0 ? products : DEFAULT_PRODUCTS;

  return (
    <section className={`w-full px-4 ${showHeader ? "py-16" : ""}`}>
      <div className="max-w-7xl mx-auto">
        {showHeader && <ProductHeader />}
        <ProductGrid
          products={displayProducts}
          onProductSelect={onProductSelect}
        />
      </div>
    </section>
  );
}
